import 'reflect-metadata';

import { ClaimsSchemaEnumValue } from '../api';
import { UdtError, UdtErrorCode } from './error';
import { LinkedCredential, LinkedFile } from './models';
import type { Ctor } from './types';

export const DECORATOR_PREFIX = 'credential-claim';
export const CLAIMS_LIST = `${DECORATOR_PREFIX}:list`;
export const CLAIM_JSON_LD_CONTEXT = `${DECORATOR_PREFIX}:ld-context`;
export const CLAIM_VC_SCHEMA_OPTIONS = `${DECORATOR_PREFIX}:vc-schema-options`;
export const CLAIM_VC_EXTERNAL_SCHEMA = `${DECORATOR_PREFIX}:vc-external-schema`;
export const CLAIM_VC_SCHEMA_JSON_LD_TERM_NAME = `${DECORATOR_PREFIX}:vc-schema-json-ld-term-name`;
export const CLAIM_JSON_LD_TYPE = `${DECORATOR_PREFIX}:ld-type`;
export const CLAIM_LINKED_FILE = `${DECORATOR_PREFIX}:linked-file`;
const DECORATOR_NAMES_LIST = `decorator-names:list`;

type VcContextOptions = {
    name: string;
    namespace: string;
    vcType?: string[];
};

type VcSchemaOptions = {
    slug?: string;
    version?: number;
    owner?: string;
};

export type VcSchemaMetadata = {
    slug?: string;
    version: number;
    owner?: string;
    vcJsonSchemas?: string[];
    jsonLdVocabs?: string[];
    jsonLdBaseIri?: string;
    jsonLdTermName?: string;
};

type VcContextMetadata = VcContextOptions;

export type ClaimDescriptor = {
    name: string;
    nestedType?: Ctor<any>;
    title?: string;
    isArray?: boolean;
    notEmpty?: boolean;
    enumValuesDescriptor?: ClaimsSchemaEnumValue[];
    jsonLdIri?: string;
    jsonLdType?: string;
};

type ClaimsListMetadata = ClaimDescriptor[];

const registerClaim = (target: object, claim: ClaimDescriptor) => {
    if (!Reflect.hasMetadata(CLAIMS_LIST, target)) {
        Reflect.defineMetadata(CLAIMS_LIST, [], target);
    }
    const claimsList: ClaimsListMetadata = Reflect.getMetadata(CLAIMS_LIST, target);

    const existingClaim = claimsList.find(({ name }) => name === claim.name);

    if (existingClaim) {
        Object.assign(existingClaim, claim);
    } else {
        claimsList.push(claim);
    }
};

const PROPERTY_DECORATOR_NAMES = {
    VC_CLAIM: 'VcClaim',
    VC_CLAIM_TITLE: 'VcClaimTitle',
    VC_NOT_EMPTY_CLAIM: 'VcNotEmptyClaim',
    VC_STRING_ENUM_CLAIM: 'VcStringEnumClaim',
    VC_ARRAY_CLAIM: 'VcArrayClaim',
    VC_CLAIM_TYPE: 'VcClaimType',
    VC_CLAIM_JSON_LD_IRI: 'VcClaimJsonLdIri',
    VC_CLAIM_JSON_LD_TYPE: 'VcClaimJsonLdType',
};

const reportDecoratorsUsage = (
    propertyKey: string | symbol,
    newPropertyDecoratorName: string,
    {
        irrelevantDueToList,
        makesObsoleteList,
        contradictsWithList,
    }: { irrelevantDueToList?: string[]; makesObsoleteList?: string[]; contradictsWithList?: string[] },
    existingDecoratorsList: string[],
) => {
    const alreadyDefinedContradictsWith = contradictsWithList
        ? existingDecoratorsList.find((value) => contradictsWithList.includes(value))
        : undefined;

    if (alreadyDefinedContradictsWith) {
        throw new Error(
            `Property decorator "${newPropertyDecoratorName}" is incompatible with existing "${alreadyDefinedContradictsWith}" on property "${String(propertyKey)}". Please review the existing decorators and remove one of them.`,
        );
    }

    if (process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'test') {
        const alreadyDefinedIrrelevantDueTo = irrelevantDueToList
            ? existingDecoratorsList.filter((value) => irrelevantDueToList.includes(value))
            : undefined;
        const alreadyDefinedMakesObsolete = makesObsoleteList
            ? existingDecoratorsList.filter((value) => makesObsoleteList.includes(value))
            : undefined;

        if (alreadyDefinedIrrelevantDueTo?.length) {
            console.warn(
                `The already defined decorator${alreadyDefinedIrrelevantDueTo.length > 1 ? 's' : ''} ("${alreadyDefinedIrrelevantDueTo.join('", "')}") on the property "${String(propertyKey)}" makes registering the new "${newPropertyDecoratorName}" decorator unnecessary. You can freely remove it.`,
            );
        }

        if (alreadyDefinedMakesObsolete?.length) {
            console.warn(
                `The already defined decorator${alreadyDefinedMakesObsolete.length > 1 ? 's' : ''} ("${alreadyDefinedMakesObsolete.join('", "')}") on the property "${String(propertyKey)}" ${alreadyDefinedMakesObsolete.length > 1 ? 'are' : 'is'} made obsolete by the new "${newPropertyDecoratorName}". You can freely remove ${alreadyDefinedMakesObsolete.length > 1 ? 'them' : 'it'}.`,
            );
        }
    }
};

/* incompatibility/overlapping property decorators table:

| property decorator      | VcClaim | VcNotEmptyClaim | VcClaimTitle | VcStringEnumClaim | VcArrayClaim | VcClaimType | VcClaimJsonLdIri | VcClaimJsonLdType |
|-------------------------|---------|-----------------|--------------|-------------------|--------------|-------------|------------------|-------------------|
| VcClaim                 | I       | I               |              | I                 | I            |             |                  |                   |
| VcNotEmptyClaim         | O       | I               |              |                   |              |             |                  |                   |
| VcClaimTitle            |         |                 | C            |                   |              |             |                  |                   |
| VcStringEnumClaim       | O       |                 |              | C                 |              |             |                  |                   |
| VcArrayClaim            | O       |                 |              |                   | C            |             |                  |                   |
| VcClaimType             | O       |                 |              |                   |              | C           |                  | C                 |
| VcClaimJsonLdIri        | O       |                 |              |                   |              |             | C                |                   |
| VcClaimJsonLdType       | O       |                 |              |                   |              | C           |                  | C                 |

Legend:
O - <row name> makes obsolete <column name> [WARNING]
I - <row name> irrelevant due to <column name> [WARNING]
C - <row name> contradicts with <column name> [ERROR]

 */
const registerDecoratorName = (target: object, propertyKey: string | symbol, decoratorName: string) => {
    if (!Reflect.hasMetadata(DECORATOR_NAMES_LIST, target, propertyKey)) {
        Reflect.defineMetadata(DECORATOR_NAMES_LIST, [], target, propertyKey);
    }
    const existingDecoratorsList: string[] = Reflect.getMetadata(DECORATOR_NAMES_LIST, target, propertyKey);

    switch (decoratorName) {
        case PROPERTY_DECORATOR_NAMES.VC_CLAIM:
            reportDecoratorsUsage(
                propertyKey,
                decoratorName,
                {
                    irrelevantDueToList: [
                        PROPERTY_DECORATOR_NAMES.VC_CLAIM,
                        PROPERTY_DECORATOR_NAMES.VC_NOT_EMPTY_CLAIM,
                        PROPERTY_DECORATOR_NAMES.VC_STRING_ENUM_CLAIM,
                        PROPERTY_DECORATOR_NAMES.VC_ARRAY_CLAIM,
                    ],
                },
                existingDecoratorsList,
            );
            break;
        case PROPERTY_DECORATOR_NAMES.VC_NOT_EMPTY_CLAIM:
            reportDecoratorsUsage(
                propertyKey,
                decoratorName,
                {
                    irrelevantDueToList: [PROPERTY_DECORATOR_NAMES.VC_NOT_EMPTY_CLAIM],
                    makesObsoleteList: [PROPERTY_DECORATOR_NAMES.VC_CLAIM],
                },
                existingDecoratorsList,
            );
            break;
        case PROPERTY_DECORATOR_NAMES.VC_CLAIM_TITLE:
            reportDecoratorsUsage(
                propertyKey,
                decoratorName,
                {
                    contradictsWithList: [PROPERTY_DECORATOR_NAMES.VC_CLAIM_TITLE],
                },
                existingDecoratorsList,
            );
            break;
        case PROPERTY_DECORATOR_NAMES.VC_STRING_ENUM_CLAIM:
            reportDecoratorsUsage(
                propertyKey,
                decoratorName,
                {
                    contradictsWithList: [PROPERTY_DECORATOR_NAMES.VC_STRING_ENUM_CLAIM],
                    makesObsoleteList: [PROPERTY_DECORATOR_NAMES.VC_CLAIM],
                },
                existingDecoratorsList,
            );
            break;
        case PROPERTY_DECORATOR_NAMES.VC_ARRAY_CLAIM:
            reportDecoratorsUsage(
                propertyKey,
                decoratorName,
                {
                    contradictsWithList: [PROPERTY_DECORATOR_NAMES.VC_ARRAY_CLAIM],
                    makesObsoleteList: [PROPERTY_DECORATOR_NAMES.VC_CLAIM],
                },
                existingDecoratorsList,
            );
            break;
        case PROPERTY_DECORATOR_NAMES.VC_CLAIM_TYPE:
            reportDecoratorsUsage(
                propertyKey,
                decoratorName,
                {
                    contradictsWithList: [
                        PROPERTY_DECORATOR_NAMES.VC_CLAIM_TYPE,
                        PROPERTY_DECORATOR_NAMES.VC_CLAIM_JSON_LD_TYPE,
                    ],
                    makesObsoleteList: [PROPERTY_DECORATOR_NAMES.VC_CLAIM],
                },
                existingDecoratorsList,
            );
            break;
        case PROPERTY_DECORATOR_NAMES.VC_CLAIM_JSON_LD_IRI:
            reportDecoratorsUsage(
                propertyKey,
                decoratorName,
                {
                    contradictsWithList: [PROPERTY_DECORATOR_NAMES.VC_CLAIM_JSON_LD_IRI],
                    makesObsoleteList: [PROPERTY_DECORATOR_NAMES.VC_CLAIM],
                },
                existingDecoratorsList,
            );
            break;
        case PROPERTY_DECORATOR_NAMES.VC_CLAIM_JSON_LD_TYPE:
            reportDecoratorsUsage(
                propertyKey,
                decoratorName,
                {
                    contradictsWithList: [
                        PROPERTY_DECORATOR_NAMES.VC_CLAIM_JSON_LD_TYPE,
                        PROPERTY_DECORATOR_NAMES.VC_CLAIM_TYPE,
                    ],
                    makesObsoleteList: [PROPERTY_DECORATOR_NAMES.VC_CLAIM],
                },
                existingDecoratorsList,
            );
            break;
    }

    existingDecoratorsList.push(decoratorName);
};

type VcExternalSchemaOptions = {
    jsonLdVocabs?: string[];
    vcJsonSchemas?: string[];
};

type VcClaimOptions = {
    title?: string;
    dataType?: Ctor<any>;
    notEmpty?: boolean;
    jsonLdIri?: string;
    jsonLdType?: string;
};

const vcSchemaMetadataFromVcContextMetadata = (vcContextMetadata: VcContextMetadata): VcSchemaMetadata => {
    return {
        slug: undefined,
        version: 1,
        owner: undefined,
        jsonLdTermName: vcContextMetadata.name,
        jsonLdBaseIri: vcContextMetadata.namespace,
    };
};

export const VcClaimWithOptions =
    ({ title, dataType, notEmpty, jsonLdIri, jsonLdType }: VcClaimOptions = {}): PropertyDecorator =>
    (target, propertyKey) => {
        if (typeof propertyKey !== 'string') return;

        let nestedType = dataType;
        const tsType: Ctor<any> = Reflect.getMetadata('design:type', target, propertyKey);

        if (
            !Array.of<Ctor<any>>(
                String,
                Number,
                Date,
                Boolean,
                Object,
                Array,
                Function,
                LinkedFile,
                LinkedCredential,
            ).includes(tsType)
        ) {
            nestedType = tsType;
        }

        const claim: ClaimDescriptor = {
            name: propertyKey,
        };

        if (nestedType != null) claim.nestedType = nestedType;
        if (tsType === Array) claim.isArray = true;
        if (title != null) claim.title = title;
        if (notEmpty != null) claim.notEmpty = notEmpty;
        if (jsonLdIri != null) claim.jsonLdIri = jsonLdIri;
        if (jsonLdType != null) claim.jsonLdType = jsonLdType;

        registerClaim(target, claim);
    };

export const VcClaim: PropertyDecorator = (target, propertyKey) => {
    VcClaimWithOptions()(target, propertyKey);

    registerDecoratorName(target, propertyKey, PROPERTY_DECORATOR_NAMES.VC_CLAIM);
};

export const VcNotEmptyClaim: PropertyDecorator = (target, propertyKey) => {
    VcClaimWithOptions({ notEmpty: true })(target, propertyKey);

    registerDecoratorName(target, propertyKey, PROPERTY_DECORATOR_NAMES.VC_NOT_EMPTY_CLAIM);
};

export const VcClaimJsonLdIri =
    (jsonLdIri: string): PropertyDecorator =>
    (target, propertyKey) => {
        VcClaimWithOptions({ jsonLdIri })(target, propertyKey);

        registerDecoratorName(target, propertyKey, PROPERTY_DECORATOR_NAMES.VC_CLAIM_JSON_LD_IRI);
    };

export const VcClaimJsonLdType =
    (jsonLdType: string): PropertyDecorator =>
    (target, propertyKey) => {
        VcClaimWithOptions({ jsonLdType })(target, propertyKey);

        registerDecoratorName(target, propertyKey, PROPERTY_DECORATOR_NAMES.VC_CLAIM_JSON_LD_TYPE);
    };

export const VcClaimTitle =
    (title: string): PropertyDecorator =>
    (target, propertyKey) => {
        VcClaimWithOptions({ title })(target, propertyKey);

        registerDecoratorName(target, propertyKey, PROPERTY_DECORATOR_NAMES.VC_CLAIM_TITLE);
    };

export const VcArrayClaim =
    (dataType: Ctor<any>): PropertyDecorator =>
    (target, propertyKey) => {
        VcClaimWithOptions({ dataType })(target, propertyKey);

        registerDecoratorName(target, propertyKey, PROPERTY_DECORATOR_NAMES.VC_ARRAY_CLAIM);
    };

export const VcStringEnumClaim =
    (value: ClaimsSchemaEnumValue[]): PropertyDecorator =>
    (target, propertyKey) => {
        if (typeof propertyKey !== 'string') return;

        const tsType: Ctor<any> = Reflect.getMetadata('design:type', target, propertyKey);

        if (tsType !== String) throw new Error(`Only string enum values allowed.`);

        VcClaimWithOptions()(target, propertyKey);
        registerClaim(target, {
            name: propertyKey,
            enumValuesDescriptor: value,
        });

        registerDecoratorName(target, propertyKey, PROPERTY_DECORATOR_NAMES.VC_STRING_ENUM_CLAIM);
    };

export const VcExternalSchema =
    (options: VcExternalSchemaOptions): ClassDecorator =>
    (target) => {
        Reflect.defineMetadata(CLAIM_VC_EXTERNAL_SCHEMA, options, target);
    };

export const VcContext =
    (options: VcContextOptions): ClassDecorator =>
    (target) => {
        const metadata: VcContextMetadata = options;
        Reflect.defineMetadata(CLAIM_JSON_LD_CONTEXT, metadata, target);
    };

export const VcClaimType =
    (jsonLdType: string): PropertyDecorator =>
    (target, propertyKey) => {
        VcClaimWithOptions({ jsonLdType })(target, propertyKey);

        registerDecoratorName(target, propertyKey, PROPERTY_DECORATOR_NAMES.VC_CLAIM_TYPE);
    };

export const VcSchema =
    (options?: VcSchemaOptions): ClassDecorator =>
    (target) => {
        Reflect.defineMetadata(CLAIM_VC_SCHEMA_OPTIONS, options || {}, target);
    };

export const VcSchemaJsonLdTermName =
    (value: string): ClassDecorator =>
    (target) => {
        Reflect.defineMetadata(CLAIM_VC_SCHEMA_JSON_LD_TERM_NAME, value, target);
    };

VcSchema.jsonLdVocabUrl = 'VC_SCHEMA_JSON_LD_VOCAB_URL';
VcSchema.vcdmJsonSchemaUrl = 'VC_SCHEMA_VCDM_JSON_SCHEMA_URL';

export const getTsDataTypeMetadata = (target: Ctor<any>, propertyKey: string): Ctor<any> | undefined =>
    Reflect.getMetadata('design:type', target.prototype, propertyKey);

export const getContextJsonLdMetadata = (target: Ctor<any>): VcContextMetadata | undefined =>
    Reflect.getMetadata(CLAIM_JSON_LD_CONTEXT, target);

export const getVcSchemaMetadata = (target: Ctor<any>): VcSchemaMetadata | undefined => {
    const vcSchemaOptions = Reflect.getMetadata(CLAIM_VC_SCHEMA_OPTIONS, target);
    if (vcSchemaOptions) {
        const vcExternalSchema = Reflect.getMetadata(CLAIM_VC_EXTERNAL_SCHEMA, target);
        const vcSchemaJsonLdTermName = Reflect.getMetadata(CLAIM_VC_SCHEMA_JSON_LD_TERM_NAME, target);
        return {
            slug: vcSchemaOptions.slug,
            version: vcSchemaOptions.version == null ? 1 : vcSchemaOptions.version,
            owner: vcSchemaOptions.owner,
            vcJsonSchemas: vcExternalSchema?.vcJsonSchemas,
            jsonLdVocabs: vcExternalSchema?.jsonLdVocabs,
            jsonLdTermName: vcSchemaJsonLdTermName || undefined,
        };
    }

    const vcContextMetadata = getContextJsonLdMetadata(target);
    if (vcContextMetadata) {
        return vcSchemaMetadataFromVcContextMetadata(vcContextMetadata);
    }

    return undefined;
};

export const getVcSchemaMetadataOrThrow = (target: Ctor<any>): VcSchemaMetadata => {
    const vcSchemaOptions = getVcSchemaMetadata(target);
    if (vcSchemaOptions == null) {
        throw new UdtError(UdtErrorCode.VcSchemaAnnotationMissing, '@VcSchema annotation is required on the UDT class');
    }
    return vcSchemaOptions;
};

export const getRegisteredClaimsList = (target: Ctor<any>): ClaimsListMetadata =>
    Reflect.getMetadata(CLAIMS_LIST, target.prototype) || [];
